import React, { useState } from 'react';
import {
  XMarkIcon,
  UserIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  LockClosedIcon,
  PauseCircleIcon,
  PlayCircleIcon
} from '@heroicons/react/24/outline';

interface Owner {
  id: string;
  name: string;
  email: string;
  phone: string;
  businessName: string;
  businessNumber: string;
  facilities: number;
  status: 'active' | 'pending' | 'suspended';
  joinDate: string;
  lastLogin: string;
  monthlyRevenue?: number;
  activeMembers?: number;
}

interface OwnerDetailModalProps {
  owner: Owner | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (owner: Owner) => void;
  onResetPassword: (ownerId: string) => void;
  onToggleSuspend: (ownerId: string, suspend: boolean) => void;
}

const OwnerDetailModal: React.FC<OwnerDetailModalProps> = ({
  owner,
  isOpen,
  onClose,
  onEdit,
  onResetPassword,
  onToggleSuspend
}) => {
  const [suspendReason, setSuspendReason] = useState('');
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);

  if (!isOpen || !owner) return null;

  const handleSuspend = () => {
    if (suspendReason.trim()) {
      onToggleSuspend(owner.id, true);
      setShowSuspendDialog(false);
      setSuspendReason('');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">오너 상세 정보</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 내용 */}
        <div className="p-6 space-y-6">
          {/* 기본 정보 */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-4">기본 정보</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <UserIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">이름</p>
                  <p className="font-medium">{owner.name}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">이메일</p>
                  <p className="font-medium">{owner.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <PhoneIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">연락처</p>
                  <p className="font-medium">{owner.phone}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">가입일</p>
                  <p className="font-medium">{owner.joinDate}</p>
                </div>
              </div>
            </div>
          </div>

          {/* 사업자 정보 */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-4">사업자 정보</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">상호명</p>
                  <p className="font-medium">{owner.businessName}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">사업자번호</p>
                  <p className="font-medium">{owner.businessNumber}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">업종</p>
                  <p className="font-medium">독서실</p>
                </div>
              </div>
            </div>
          </div>

          {/* 운영 현황 */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-4">운영 현황</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center space-x-3">
                  <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">등록 좌석</p>
                    <p className="text-2xl font-bold text-gray-900">{owner.facilities * 50}석</p>
                  </div>
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center space-x-3">
                  <UsersIcon className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">활성 회원</p>
                    <p className="text-2xl font-bold text-gray-900">{owner.activeMembers || 0}명</p>
                  </div>
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
                  <div>
                    <p className="text-sm text-gray-600">이번달 매출</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {owner.monthlyRevenue?.toLocaleString() || 0}원
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 계정 상태 */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-4">계정 상태</h4>
            <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">현재 상태</p>
                <p className="font-medium">
                  {owner.status === 'active' && <span className="text-green-600">활성</span>}
                  {owner.status === 'suspended' && <span className="text-red-600">정지</span>}
                  {owner.status === 'pending' && <span className="text-yellow-600">승인대기</span>}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">최근 로그인</p>
                <p className="font-medium">{owner.lastLogin}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">권한</p>
                <p className="font-medium">오너</p>
              </div>
            </div>
          </div>

          {/* 관리 기능 */}
          <div className="border-t pt-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4">관리 기능</h4>
            <div className="flex space-x-3">
              <button
                onClick={() => onResetPassword(owner.id)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <LockClosedIcon className="h-4 w-4 mr-2" />
                비밀번호 초기화
              </button>
              
              {owner.status === 'active' ? (
                <button
                  onClick={() => setShowSuspendDialog(true)}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  <PauseCircleIcon className="h-4 w-4 mr-2" />
                  계정 정지
                </button>
              ) : (
                <button
                  onClick={() => onToggleSuspend(owner.id, false)}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  <PlayCircleIcon className="h-4 w-4 mr-2" />
                  계정 활성화
                </button>
              )}
              
              <button
                onClick={() => onEdit(owner)}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                정보 수정
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 계정 정지 다이얼로그 */}
      {showSuspendDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">계정 정지</h3>
            <p className="text-gray-600 mb-4">정지 사유를 입력해주세요.</p>
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="정지 사유를 입력하세요..."
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setShowSuspendDialog(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                취소
              </button>
              <button
                onClick={handleSuspend}
                disabled={!suspendReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                정지
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerDetailModal;